import * as React from 'react';
const { forwardRef } = React;
const h = React.createElement;
import { sizeClasses } from '../shared';

/**
 * ghost 与 text 的区别：
 *   text 带 p-0（贴合纯文字链接场景，如表格内的行内操作）；
 *   ghost 保留 sizeClasses 的内边距，只去掉背景与边框，
 *   用于「图标 + 文字」的常规功能按钮（见 ActionButton）。
 */
export type ButtonType = 'default' | 'primary' | 'dashed' | 'link' | 'text' | 'ghost';
export type ButtonSize = 'small' | 'middle' | 'large';

export interface ButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'size'
> {
  type?: ButtonType;
  size?: ButtonSize;
  icon?: React.ReactNode;
  loading?: boolean;
  danger?: boolean;
  block?: boolean;
  htmlType?: 'button' | 'submit' | 'reset';
  children?: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const {
    type = 'default',
    size = 'middle',
    icon,
    children,
    className = '',
    loading = false,
    disabled = false,
    danger = false,
    block = false,
    htmlType,
    onClick,
    ...rest
  } = props;

  const sizeClass = sizeClasses[size] || sizeClasses.middle;
  let typeClass: string;
  if (danger) {
    typeClass =
      type === 'primary'
        ? 'bg-red-600 hover:bg-red-500 text-white border-transparent'
        : 'bg-transparent border-transparent text-red-400 hover:bg-red-500/10 hover:text-red-300';
  } else {
    const typeMap: Record<ButtonType, string> = {
      primary:
        'bg-primary-600 hover:bg-primary-500 text-white border-transparent shadow-lg shadow-primary-600/20',
      default: 'bg-dark-700 hover:bg-dark-600 text-dark-200 border-dark-600',
      dashed:
        'bg-transparent border-dashed border-dark-600 text-dark-300 hover:border-primary-500 hover:text-primary-400',
      link: 'bg-transparent border-transparent text-primary-400 hover:text-primary-300 p-0',
      text: 'bg-transparent border-transparent text-dark-300 hover:text-dark-100 hover:bg-dark-700 p-0',
      ghost: 'bg-transparent border-transparent text-dark-300 hover:text-dark-100 hover:bg-dark-700',
    };
    typeClass = typeMap[type] || typeMap.default;
  }

  const blockClass = block ? 'w-full' : '';
  const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : '';
  const linkSizeMap: Record<ButtonSize, string> = {
    small: 'text-xs',
    middle: 'text-sm',
    large: 'text-base',
  };
  const linkSizeClass = type === 'link' || type === 'text' ? linkSizeMap[size] || 'text-sm' : '';

  return h(
    'button',
    {
      ref,
      type: htmlType || 'button',
      className:
        `inline-flex items-center justify-center rounded-lg border font-medium transition-all duration-200 ${sizeClass} ${typeClass} ${blockClass} ${disabledClass} ${linkSizeClass} ${className}`.trim(),
      disabled: disabled || loading,
      onClick,
      ...rest,
    },
    loading &&
      h(
        'svg',
        { className: 'animate-spin -ml-1 mr-2 h-4 w-4', fill: 'none', viewBox: '0 0 24 24' },
        h('circle', {
          className: 'opacity-25',
          cx: '12',
          cy: '12',
          r: '10',
          stroke: 'currentColor',
          strokeWidth: '4',
        }),
        h('path', {
          className: 'opacity-75',
          fill: 'currentColor',
          d: 'M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z',
        }),
      ),
    !loading &&
      icon &&
      h('span', { className: type === 'link' || type === 'text' ? 'mr-1' : 'mr-2' }, icon),
    children,
  );
});

export { Button };
